/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import { useRef, useState, useEffect } from 'react';
import { Intersection } from '@splidejs/splide-extension-intersection';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/splide/css';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { tv } from 'tailwind-variants';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getTestProps } from 'lib/testing/utils';
import useIsEditing from 'lib/hooks/useIsEditing';
import ClientStoriesCarouselItem from './ClientStoriesCarouselItem';
import RemixIcon from 'helpers/RemixIcon/RemixIcon';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const SPLIDE_INTERVAL = 3000;
const SPLIDE_SPEED = 300;
const CarouselArrows = ({ splideRef, syncIndexRef, slideArrows, actionButtonControl })=>{
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalSlides, setTotalSlides] = useState(0);
    const [perPage, setPerPage] = useState(1);
    useEffect(()=>{
        syncIndexRef.current = (index, total, perPageCount)=>{
            setCurrentIndex(index);
            setTotalSlides(total);
            setPerPage(Math.max(1, perPageCount));
        };
        return ()=>{
            syncIndexRef.current = ()=>{};
        };
    }, [
        syncIndexRef
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: slideArrows(),
        children: [
            /*#__PURE__*/ _jsx("button", {
                onClick: ()=>splideRef.current?.go('<'),
                className: actionButtonControl({
                    isArrowDisabled: currentIndex === 0
                }),
                "aria-label": "Previous slide",
                disabled: currentIndex === 0,
                ...getTestProps(`Previous slide`),
                children: /*#__PURE__*/ _jsx(RemixIcon, {
                    name: "arrow-left-line",
                    size: "md"
                })
            }),
            /*#__PURE__*/ _jsx("button", {
                onClick: ()=>splideRef.current?.go('>'),
                className: actionButtonControl({
                    isArrowDisabled: currentIndex === totalSlides - perPage
                }),
                "aria-label": "Next slide",
                disabled: currentIndex === totalSlides - perPage,
                ...getTestProps(`Next slide`),
                children: /*#__PURE__*/ _jsx(RemixIcon, {
                    name: "arrow-right-line",
                    size: "md"
                })
            })
        ]
    });
};
const ClientStoriesCarousel = (props)=>{
    const { RenderingIdentifier } = props?.params || {};
    const { title, description, cardItemList } = props?.fields || {};
    const cardItems = cardItemList ?? [];
    const theme = props?.params?.selectTheme;
    const splideRef = useRef(null);
    const syncIndexRef = useRef(()=>{});
    // Provider wraps content so children (e.g., NewsList) can set availability and hide this section.
    const isEditing = useIsEditing();
    const splideOptions = {
        autoplay: false,
        gap: '8px',
        arrows: false,
        intersection: {
            inView: {
                autoplay: false
            },
            outView: {
                autoplay: false
            }
        },
        interval: SPLIDE_INTERVAL,
        pagination: true,
        perMove: 1,
        perPage: 3,
        focus: 0,
        omitEnd: true,
        rewind: false,
        speed: SPLIDE_SPEED,
        type: 'slide',
        width: '100%',
        drag: true,
        rewindByDrag: false,
        padding: {
            right: '26%'
        },
        breakpoints: {
            1024: {
                perPage: 2,
                padding: {
                    right: 0
                }
            },
            767: {
                perPage: 1
            }
        }
    };
    /*
   * Event Handers
   */ const onMoved = (instance)=>{
        const perPageCount = typeof instance.options.perPage === 'number' ? instance.options.perPage : 1;
        syncIndexRef.current(instance.index, instance.length, perPageCount);
    };
    const { base, sectionTitle, sectionDescription, sectionContentWrapper, sectionTitleWrapper, sectionDivider, sectionWrapper, slideArrows, customPagination, actionButtonControl } = TAILWIND_VARIANTS();
    /*
   * RENDERING
   */ const ClientStoriesCarouselContent = ()=>{
        if (!isEditing && cardItems.length === 0) return /*#__PURE__*/ _jsx(_Fragment, {});
        return /*#__PURE__*/ _jsx("div", {
            "data-component": "authorable/shared/layout/clientstoriescarousel",
            ...editingHydrationProps(isEditing),
            id: RenderingIdentifier,
            ...getTestProps(`component-clientstoriescarousel-${props?.rendering?.uid}`),
            className: sectionWrapper(),
            children: /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
                theme: theme,
                children: /*#__PURE__*/ _jsx("div", {
                    className: base(),
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: sectionContentWrapper({
                            hasContent: Boolean(title?.value || description?.value || cardItems.length > 0)
                        }),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: sectionTitleWrapper(),
                                children: [
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        className: sectionTitle(),
                                        field: title,
                                        tag: "h2"
                                    }),
                                    /*#__PURE__*/ _jsx(RichTextWrapper, {
                                        className: sectionDescription(),
                                        field: description,
                                        tag: "p"
                                    }),
                                    title?.value || description?.value ? /*#__PURE__*/ _jsx("span", {
                                        className: sectionDivider()
                                    }) : null
                                ]
                            }),
                            cardItems.length > 0 && /*#__PURE__*/ _jsx(CarouselArrows, {
                                splideRef: splideRef,
                                syncIndexRef: syncIndexRef,
                                slideArrows: slideArrows,
                                actionButtonControl: actionButtonControl
                            }),
                            /*#__PURE__*/ _jsxs(Splide, {
                                ref: (ref)=>{
                                    if (ref?.splide) {
                                        splideRef.current = ref.splide;
                                    }
                                },
                                extensions: {
                                    Intersection
                                },
                                hasTrack: false,
                                onMounted: onMoved,
                                onMoved: onMoved,
                                options: splideOptions,
                                children: [
                                    /*#__PURE__*/ _jsx(SplideTrack, {
                                        className: "lg:w-screen lg:mr-[calc(50%-50vw)]",
                                        children: cardItems.map((item, index)=>/*#__PURE__*/ _jsx(SplideSlide, {
                                                ...getTestProps(`slide-${index}`),
                                                className: "p-[8px]",
                                                children: /*#__PURE__*/ _jsx(ClientStoriesCarouselItem, {
                                                    ...item
                                                })
                                            }, item.rendering?.uid))
                                    }),
                                    /*#__PURE__*/ _jsx("ul", {
                                        className: `splide__pagination ${customPagination()}`
                                    })
                                ]
                            })
                        ]
                    })
                })
            })
        });
    };
    return /*#__PURE__*/ _jsx(ClientStoriesCarouselContent, {});
};
export const Default = withStandardComponentWrapper(ClientStoriesCarousel, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        sectionWrapper: [
            'scroll-mt-[64px]',
            'md:scroll-mt-0'
        ],
        base: [
            'flex',
            'bg-component-section-bg',
            'gap-component-section-container-gutter',
            'flex-wrap',
            'relative'
        ],
        sectionTitleWrapper: [
            'flex',
            'flex-col',
            'gap-space-between-base',
            'max-w-columns-two-third-max-width'
        ],
        sectionTitle: [
            'text-component-section-title',
            'font-typography-font-family-header',
            'text-typography-font-size-xlarge',
            'leading-typography-line-height-xlarge'
        ],
        sectionDescription: [
            'text-component-section-body',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large'
        ],
        sectionContentWrapper: [
            'flex',
            'flex-col',
            'w-full',
            'h-auto'
        ],
        sectionDivider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ],
        buttonWrapper: [
            'flex',
            'gap-spacing-spacing-4'
        ],
        slideArrows: [
            'flex',
            'justify-end',
            'gap-2'
        ],
        customPagination: [
            'p-0',
            'pt-component-section-container-gutter',
            'flex',
            'justify-center',
            'static',
            '[&>li]:p-[6px]',
            '[&>li>button]:w-spacing-spacing-12',
            '[&>li>button]:h-spacing-spacing-12',
            '[&>li>button]:bg-transparent',
            '[&>li>button]:!border',
            '[&>li>button]:!border-color-core-color-1-Navy',
            '[&>li>button]:!border-solid',
            '[&>li>button]:rounded-full',
            '[&>li>button]:m-0',
            '[&>li>button.is-active]:bg-color-core-color-1-Navy',
            '[&>li>button.is-active]:transform-none',
            '[&>li>button.is-active]:opacity-100',
            '[&>li>button.is-active]:w-spacing-spacing-40'
        ],
        actionButtonControl: [
            'p-2',
            'border-border-width-width-025',
            'border-color-core-color-4-SkyBlue',
            'text-color-core-color-4-SkyBlue',
            'rounded-full',
            'w-11',
            'h-11'
        ]
    },
    variants: {
        textAlignment: {
            Left: {
                base: 'text-left'
            },
            Center: {
                base: 'text-center',
                sectionTitleWrapper: 'justify-center mx-auto',
                sectionContentWrapper: 'justify-center',
                sectionDivider: 'mx-auto',
                buttonWrapper: 'justify-center'
            },
            Right: {
                base: 'text-right',
                sectionTitleWrapper: 'justify-end',
                sectionContentWrapper: 'justify-end',
                sectionDivider: 'ml-auto',
                buttonWrapper: 'justify-end'
            }
        },
        backgroundLayout: {
            FullWidth: {
                base: [
                    'before:z-[-1]',
                    'before:h-full',
                    'before:absolute',
                    'before:bg-component-section-bg',
                    'before:top-0',
                    'before:left-[calc(-50vw+50%)]',
                    'before:right-[calc(-50vw+50%)]'
                ]
            },
            Content: {
                base: []
            }
        },
        hasContent: {
            true: {
                sectionContentWrapper: 'gap-component-section-container-gutter'
            },
            false: {
                sectionContentWrapper: 'gap-0'
            }
        },
        removePaddingTop: {
            true: {
                base: [
                    'pt-0'
                ]
            },
            false: {
                base: [
                    'pt-component-section-container-padding-y'
                ]
            }
        },
        removePaddingBottom: {
            true: {
                base: [
                    'pb-0'
                ]
            },
            false: {
                base: [
                    'pb-component-section-container-padding-y'
                ]
            }
        },
        removePaddingLeftRight: {
            true: {
                base: []
            },
            false: {
                base: [
                    'px-component-section-container-padding-x'
                ]
            }
        },
        displayCenter75: {
            true: {
                sectionContentWrapper: [
                    'max-w-layout-container-center70-max-width',
                    'mx-auto'
                ]
            },
            false: {
                sectionContentWrapper: []
            }
        },
        isArrowDisabled: {
            true: {
                actionButtonControl: [
                    'opacity-50'
                ]
            }
        }
    },
    defaultVariants: {
        textAlignment: 'Center',
        hasContent: false,
        removePaddingTop: false,
        removePaddingBottom: false,
        removePaddingLeftRight: true,
        displayCenter75: false,
        backgroundLayout: 'FullWidth'
    }
});
